import { useRef } from 'react';

import Box from '@zenbusiness/zui/Box';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Paper from '@zenbusiness/zui/Paper';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';

import AdditionalPaymentInfo from '#client/components/AdditionalPaymentInfo';
import FormInfoModal from '#client/components/FormInfoModal';
import { ActionInfoButtonsGrid } from '#client/components/Grid';
import DetailWrapper from '#client/components/TransactionDetailModal/components/DetailWrapper';
import UserDetail from '#client/components/TransactionDetailModal/components/UserDetail';
import {
  PAYMENT_STATUS,
  PAYMENT_TYPE_WITH_CHECKING_ACCOUNT,
  TRANSACTION_DIRECTION
} from '#client/constants/transaction';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useUnitAccount from '#client/hooks/useUnitAccount';

import PaymentDetail from './PaymentDetail';
import { ButtonStyled, PrintedBox } from './PaymentDetailModal.styles';

export default function PaymentDetailModal({
  amount,
  createdAt,
  estimatedCompletionDate,
  type,
  id,
  status,
  reason,
  sameDay,
  description,
  direction,
  from,
  to,
  handleClose,
  summary,
  transactionId,
  refetchPayment,
  counterparty,
  counterpartyIdFromTransaction
}) {
  const contentRef = useRef(null);
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { unitAccount } = useUnitAccount();

  const handlePrint = useReactToPrint({
    content: () => contentRef.current
  });

  useModalDisplayTracker({
    elementName: 'transactions_details_modal',
    elementText: 'transactions_details_modal',
    appPageName: 'transactions_details_modal'
  });

  return (
    <FormInfoModal
      borderRadius={isDesktop ? '16px' : '0'}
      fullScreen={!isDesktop}
      handleClose={handleClose}
      title={summary}
    >
      <DialogContent sx={{ padding: 0 }}>
        <DetailWrapper>
          <PrintedBox ref={contentRef}>
            <Paper variant="outlined">
              <PaymentDetail
                amount={amount}
                createdAt={createdAt}
                description={description}
                estimatedCompletionDate={estimatedCompletionDate}
                id={id}
                reason={reason}
                sameDay={sameDay}
                status={status}
                type={type}
              />
              {PAYMENT_TYPE_WITH_CHECKING_ACCOUNT.includes(type) &&
              direction === TRANSACTION_DIRECTION.DEBIT ? (
                <UserDetail
                  accountHolder={unitAccount?.name}
                  accountNumber={unitAccount?.accountNumber}
                  bank="ZenBusiness"
                  fullAccountNumber={unitAccount?.accountNumber}
                  title="To"
                />
              ) : (
                <UserDetail
                  accountHolder={to?.accountHolder}
                  accountNumber={to?.accountNumberLast4}
                  counterparty={counterparty}
                  counterpartyIdFromTransaction={counterpartyIdFromTransaction}
                  fullAccountNumber={to?.accountNumber}
                  routingNumber={to?.routingNumber}
                  title="To"
                />
              )}
              {PAYMENT_TYPE_WITH_CHECKING_ACCOUNT.includes(type) &&
              direction === TRANSACTION_DIRECTION.CREDIT ? (
                <UserDetail
                  accountHolder={unitAccount?.name}
                  accountNumber={unitAccount?.accountNumber}
                  bank="ZenBusiness"
                  fullAccountNumber={unitAccount?.accountNumber}
                  title="From"
                />
              ) : (
                <UserDetail
                  accountHolder={from?.accountHolder}
                  accountNumber={from?.accountNumberLast4}
                  counterparty={counterparty}
                  counterpartyIdFromTransaction={counterpartyIdFromTransaction}
                  fullAccountNumber={from?.accountNumber}
                  routingNumber={from?.routingNumber}
                  title="From"
                />
              )}
            </Paper>
          </PrintedBox>
          <AdditionalPaymentInfo
            handlePrint={handlePrint}
            id={id}
            isPending={status === PAYMENT_STATUS.PENDING && !transactionId}
            refetchPayment={refetchPayment}
          />
        </DetailWrapper>
      </DialogContent>
      {!isDesktop && (
        <ActionInfoButtonsGrid>
          <Box>
            <ButtonStyled
              color="primary"
              onClick={handleClose}
              size="large"
              variant="text"
            >
              Close
            </ButtonStyled>
          </Box>
        </ActionInfoButtonsGrid>
      )}
    </FormInfoModal>
  );
}

PaymentDetailModal.propTypes = {
  summary: PropTypes.string,
  amount: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  estimatedCompletionDate: PropTypes.string,
  type: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  reason: PropTypes.string,
  sameDay: PropTypes.bool,
  description: PropTypes.string,
  transactionId: PropTypes.string,
  direction: PropTypes.string,
  from: PropTypes.shape({
    accountHolder: PropTypes.string,
    routingNumber: PropTypes.string,
    accountNumberLast4: PropTypes.string,
    accountNumber: PropTypes.string
  }),
  to: PropTypes.shape({
    accountHolder: PropTypes.string,
    routingNumber: PropTypes.string,
    accountNumberLast4: PropTypes.string,
    accountNumber: PropTypes.string
  }),
  refetchPayment: PropTypes.func,
  handleClose: PropTypes.func,
  counterparty: PropTypes.shape({
    counterpartyId: PropTypes.string,
    bank: PropTypes.string,
    connectedType: PropTypes.string,
    accountNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string
  }),
  counterpartyIdFromTransaction: PropTypes.string
};
